import copy from '../copy/en/signing.json' with { type: 'json' };
import { createSignature } from './signature.js';
import { cleanSignatureImage, readSignatureImage, SignatureImageError, type SignatureRaster } from './signature-image.js';

/** Public practice form only. Accepted image bytes never depend on canvas resize. */
export function createPracticeSignature(canvas: HTMLCanvasElement, onChange: () => void) {
  const doc = canvas.ownerDocument;
  const win = doc.defaultView!;
  const find = <T extends HTMLElement = HTMLElement>(id: string): T => doc.getElementById(id) as T;
  const drawing = createSignature(canvas);
  const dialog = find<HTMLDialogElement>('signatureImageDialog');
  const file = find<HTMLInputElement>('signatureImageFile');
  const video = find<HTMLVideoElement>('signatureCamera');
  const candidateImage = find<HTMLImageElement>('signatureCandidate');
  const imagePreview = find('signatureImagePreview');
  const imageStatus = find('signatureImageStatus');
  const imageError = find('signatureImageError');
  const cleanup = find<HTMLInputElement>('signatureCleanup');
  const cleanupField = find('signatureCleanupField');
  const use = find<HTMLButtonElement>('useSignatureImage');
  const capture = find<HTMLButtonElement>('captureSignaturePhoto');
  const retake = find<HTMLButtonElement>('retakeSignaturePhoto');
  const selected = find<HTMLImageElement>('importedSignatureImage');
  let enabled = true;
  let disposed = false;
  let generation = 0;
  let png = '';
  let candidate = '';
  let raster: SignatureRaster | null = null;
  let stream: MediaStream | null = null;
  const listeners: (() => void)[] = [];
  const listen = (target: EventTarget, event: string, handler: EventListener) => {
    target.addEventListener(event, handler);
    listeners.push(() => target.removeEventListener(event, handler));
  };
  const allowed = () => enabled && !disposed;
  const current = (id: number) => allowed() && generation === id && dialog.open;
  // The invitation auto-sizes its iframe to the entire form. Center in the
  // visible part of that frame, rather than its much taller layout viewport.
  const host = win.frameElement;
  function positionDialog() {
    if (!host || !dialog.open) return;
    const top = host.getBoundingClientRect().top;
    const start = Math.max(0, -top);
    const height = Math.max(0, Math.min(win.innerHeight, win.parent.innerHeight - top) - start);
    dialog.style.position = 'fixed';
    dialog.style.marginBlock = '0';
    dialog.style.bottom = 'auto';
    dialog.style.maxHeight = `${Math.max(160, height - 24)}px`;
    dialog.style.top = `${start + Math.max(12, (height - dialog.getBoundingClientRect().height) / 2)}px`;
    video.style.maxHeight = `${Math.max(100, height * .42)}px`;
  }
  const dialogObserver = host && typeof win.ResizeObserver === 'function' ? new win.ResizeObserver(positionDialog) : null;
  dialogObserver?.observe(dialog);
  if (host) { listen(win.parent, 'scroll', positionDialog); listen(win.parent, 'resize', positionDialog); }
  function stopCamera() {
    stream?.getTracks().forEach(track => track.stop());
    stream = null;
    video.srcObject = null;
    video.classList.add('hidden');
    capture.disabled = true;
  }
  function resetCandidate() {
    generation++;
    stopCamera();
    candidate = ''; raster = null;
    candidateImage.removeAttribute('src');
    file.value = '';
    imagePreview.classList.add('hidden'); cleanupField.classList.add('hidden');
    capture.classList.add('hidden'); retake.classList.add('hidden');
    use.disabled = true;
    imageStatus.textContent = ''; imageError.textContent = '';
  }
  function dismiss() {
    resetCandidate();
    if (dialog.open) dialog.close();
    if (allowed() && !png) drawing.pad.on();
  }
  function open() {
    resetCandidate();
    drawing.pad.off();
    if (!dialog.open) dialog.showModal();
    positionDialog();
    return generation;
  }
  function showError(error: unknown) {
    const messages = {
      unsupported: copy.signatureImageUnsupported, too_large: copy.signatureImageTooLarge,
      unreadable: copy.signatureImageUnreadable, no_ink: copy.signatureImageNoInk,
      too_dense: copy.signatureImageTooDense,
    };
    imageError.textContent = error instanceof SignatureImageError ? messages[error.code] : copy.signatureImageUnreadable;
    imageStatus.textContent = '';
  }
  function prepare() {
    candidate = ''; use.disabled = true; imageError.textContent = '';
    candidateImage.removeAttribute('src'); imagePreview.classList.add('hidden');
    if (!raster) return;
    cleanupField.classList.remove('hidden');
    try {
      candidate = cleanSignatureImage(raster, Number(cleanup.value)).png;
      candidateImage.src = candidate;
      imagePreview.classList.remove('hidden');
      use.disabled = false;
      imageStatus.textContent = copy.signatureImageReady;
    } catch (error) { showError(error); }
    positionDialog();
  }
  function chooseFile() {
    if (!allowed()) return;
    // A pending camera permission request must not start a stream after switching.
    generation++;
    stopCamera();
    file.value = '';
    file.click();
  }
  async function loadFile() {
    const input = file.files?.[0];
    if (!input || !allowed()) return;
    const id = open();
    imageStatus.textContent = copy.signatureImageLoading;
    cleanup.value = '190';
    try {
      const decoded = await readSignatureImage(input);
      if (!current(id)) return;
      raster = decoded;
      prepare();
    } catch (error) { if (current(id)) showError(error); }
    finally { file.value = ''; }
  }
  async function startCamera() {
    if (!allowed()) return;
    const id = open();
    retake.classList.remove('hidden');
    use.classList.add('hidden');
    capture.classList.remove('hidden'); capture.disabled = true;
    imageStatus.textContent = copy.signatureCameraStarting;
    try {
      if (!win.navigator.mediaDevices?.getUserMedia) throw new Error('unavailable');
      const acquired = await win.navigator.mediaDevices.getUserMedia({ audio: false, video: { facingMode: { ideal: 'environment' }, width: { ideal: 1600 }, height: { ideal: 900 } } });
      if (!current(id)) { acquired.getTracks().forEach(track => track.stop()); return; }
      stream = acquired;
      video.srcObject = stream;
      video.classList.remove('hidden');
      await video.play();
      if (!current(id)) return;
      imageStatus.textContent = copy.signatureCameraReady;
      capture.disabled = !video.videoWidth || !video.videoHeight;
      capture.focus();
    } catch {
      if (!current(id)) return;
      stopCamera();
      imageStatus.textContent = '';
      imageError.textContent = copy.signatureCameraUnavailable;
      imageError.focus();
    }
  }
  function takePhoto() {
    if (!allowed() || !stream || !video.videoWidth || !video.videoHeight) return;
    const width = video.videoWidth, height = video.videoHeight;
    const scale = Math.min(1, 1600 / Math.max(width, height), Math.sqrt(1_000_000 / (width * height)));
    try {
      const shot = doc.createElement('canvas');
      shot.width = Math.max(1, Math.floor(width * scale)); shot.height = Math.max(1, Math.floor(height * scale));
      const ctx = shot.getContext('2d'); if (!ctx) throw new Error('unavailable');
      ctx.drawImage(video, 0, 0, shot.width, shot.height);
      raster = { width: shot.width, height: shot.height, data: ctx.getImageData(0, 0, shot.width, shot.height).data };
      cleanup.value = '190';
      prepare();
    } catch (error) { showError(error); }
    finally {
      stopCamera();
      capture.classList.add('hidden'); use.classList.remove('hidden');
    }
    if (!use.disabled) use.focus();
  }
  function clear() {
    dismiss();
    png = '';
    selected.removeAttribute('src'); find('importedSignature').classList.add('hidden');
    canvas.classList.remove('hidden');
    drawing.pad.clear(); drawing.resize();
    if (allowed()) drawing.pad.on();
    onChange();
  }
  listen(find('uploadSignature'), 'click', chooseFile);
  listen(find('chooseSignatureImage'), 'click', () => { use.classList.remove('hidden'); chooseFile(); });
  listen(find('photoSignature'), 'click', () => { void startCamera(); });
  listen(retake, 'click', () => { void startCamera(); });
  listen(file, 'change', () => { use.classList.remove('hidden'); void loadFile(); });
  listen(cleanup, 'input', prepare);
  listen(capture, 'click', takePhoto);
  listen(video, 'loadeddata', () => { if (allowed() && stream) capture.disabled = !video.videoWidth || !video.videoHeight; });
  listen(use, 'click', () => {
    if (!allowed() || !candidate || !dialog.open) return;
    png = candidate;
    selected.src = png;
    canvas.classList.add('hidden'); find('importedSignature').classList.remove('hidden');
    drawing.pad.clear(); drawing.pad.off();
    dismiss();
    onChange();
    find('typedName').focus();
  });
  listen(find('cancelSignatureImage'), 'click', dismiss);
  listen(dialog, 'cancel', event => { event.preventDefault(); dismiss(); });
  listen(dialog, 'close', () => {
    // Native close events are queued; an earlier dialog may have been reopened.
    if (dialog.open) return;
    resetCandidate(); if (allowed() && !png) drawing.pad.on();
  });
  listen(doc, 'visibilitychange', () => { if (doc.visibilityState === 'hidden') dismiss(); });
  listen(win, 'pagehide', dismiss);
  listen(win, 'message', event => {
    const message = event as MessageEvent;
    if (win.parent !== win && message.source === win.parent && message.origin === win.location.origin
      && message.data?.type === 'ohf:signer:visibility' && message.data.visible === false) dismiss();
  });
  return {
    isEmpty: () => !png && drawing.pad.isEmpty(),
    exportPng: () => png || drawing.exportPng(),
    resize: drawing.resize,
    clear,
    get pending() { return dialog.open; },
    setEnabled(value: boolean) {
      enabled = value;
      if (!value) { dismiss(); drawing.pad.off(); }
      else if (!png && !dialog.open) drawing.pad.on();
    },
    dispose() {
      disposed = true; dismiss();
      dialogObserver?.disconnect();
      listeners.forEach(remove => remove()); drawing.dispose();
      png = ''; selected.removeAttribute('src');
    },
  };
}
